import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Lock } from 'lucide-react';
import { SINGLE_EPISODE_PRICE } from '../config/pricing';

const CustomVideoPlayer = ({ src, title, className = "", downloadHref = null, fallbackAudioSrc = null, isPreview = false, onUnlock = null, credits = 0 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);

  // Use video if available, fallback to audio
  const useAudioFallback = hasVideoError || !src;
  const mediaRef = useAudioFallback ? audioRef : videoRef;
  const mediaSrc = useAudioFallback ? fallbackAudioSrc : src;

  useEffect(() => {
    const media = mediaRef.current;
    if (!media || !mediaSrc) return;

    // Set the src directly on the media element
    media.src = mediaSrc;
    media.load(); // Force reload
    setShowPaywall(false);

    const updateTime = () => {
      setCurrentTime(media.currentTime);
      setProgress((media.currentTime / media.duration) * 100);
    };

    const updateDuration = () => {
      setDuration(media.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      // Preview clips are only 60s — surface the paywall when they finish.
      if (isPreview) setShowPaywall(true);
    };

    const handleError = (e) => {
      console.error('Media loading error:', e);
      console.error('Media src:', mediaSrc);
      if (!useAudioFallback && fallbackAudioSrc) {
        console.log('Falling back to audio player');
        setHasVideoError(true);
      }
    };

    media.addEventListener('timeupdate', updateTime);
    media.addEventListener('loadedmetadata', updateDuration);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('error', handleError);

    return () => {
      media.removeEventListener('timeupdate', updateTime);
      media.removeEventListener('loadedmetadata', updateDuration);
      media.removeEventListener('ended', handleEnded);
      media.removeEventListener('error', handleError);
    };
  }, [mediaSrc, useAudioFallback, fallbackAudioSrc, mediaRef, isPreview]);

  // Keep the media element's muted state in sync with UI state
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.muted = isMuted;
    }
  }, [isMuted, useAudioFallback, mediaRef]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Fullscreen state is tracked by document.fullscreenElement
      // No need to store in component state since we check it directly
    };

    // Add event listeners for all browser prefixes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Simple static visualization bars for audio fallback
  const renderStaticBars = () => {
    const bars = [];
    for (let i = 0; i < 20; i++) {
      const height = Math.random() * 20 + 5;
      bars.push(
        <div
          key={i}
          className="bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-sm"
          style={{
            width: '3px',
            height: `${height}px`,
            opacity: isPlaying ? 0.8 : 0.3
          }}
        />
      );
    }
    return bars;
  };

  const togglePlayPause = async () => {
    const media = mediaRef.current;
    if (!media) return;

    try {
      if (isPlaying) {
        media.pause();
        setIsPlaying(false);
      } else {
        // Replaying after the preview ended — hide the paywall overlay first.
        if (showPaywall) setShowPaywall(false);
        await media.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error playing media:', error);
      console.error('Media src:', mediaSrc);
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const media = mediaRef.current;
    if (!media) return;
    const next = !isMuted;
    media.muted = next;
    setIsMuted(next);
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current || useAudioFallback) return;

    try {
      // Check if we're already in fullscreen
      const isFullscreen = document.fullscreenElement || 
                          document.webkitFullscreenElement || 
                          document.mozFullScreenElement || 
                          document.msFullscreenElement;

      if (!isFullscreen) {
        // Try different fullscreen methods for cross-browser compatibility
        const element = containerRef.current;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen with cross-browser support
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Fallback: try to make the video element itself fullscreen
      try {
        const video = videoRef.current;
        if (video && video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        }
      } catch (fallbackError) {
        console.error('Video fullscreen fallback failed:', fallbackError);
      }
    }
  };

  const handleProgressClick = (e) => {
    const media = mediaRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    media.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div ref={containerRef} className={`bg-gray-900 rounded-xl overflow-hidden ${className}`}>
      {/* Video Display */}
      {!useAudioFallback && (
        <div className="relative w-full max-w-sm mx-auto aspect-square bg-black rounded-t-xl overflow-hidden">
          <video 
            ref={videoRef}
            className="w-full h-full object-cover"
            preload="metadata"
            playsInline
          >
            <source src={src} type="video/mp4" />
            Your browser does not support the video element.
          </video>
          
          {/* Preview badge */}
          {isPreview && !showPaywall && (
            <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-black/70 text-yellow-400 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md">
              <Lock className="h-3 w-3" />Preview
            </div>
          )}

          {/* Video Overlay Controls */}
          {!showPaywall && (
            <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
              <button 
                onClick={togglePlayPause}
                className="bg-black/50 text-white hover:bg-black/70 p-4 rounded-full transition-all duration-200"
              >
                {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
              </button>
            </div>
          )}

          {/* Paywall overlay — shown when the preview clip ends */}
          {isPreview && showPaywall && (
            <div className="absolute inset-0 z-20 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6">
              <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center mb-3 shadow-lg">
                <Lock className="h-6 w-6 text-gray-900" />
              </div>
              <p className="text-white font-black text-base mb-1">That's the preview!</p>
              <p className="text-gray-300 text-xs mb-4 max-w-[16rem]">Unlock the full episode to hear the entire podcast and share it with fans</p>
              {onUnlock && (
                <button
                  onClick={onUnlock}
                  className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black py-2 px-5 rounded-xl transition-colors shadow-lg text-sm"
                >
                  <Lock className="h-4 w-4" />
                  {credits >= 1 ? 'Unlock Full Episode — Use 1 Credit' : `Unlock Full Episode — ${SINGLE_EPISODE_PRICE}`}
                </button>
              )}
              <button
                onClick={togglePlayPause}
                className="mt-3 text-gray-400 hover:text-white text-xs font-semibold underline"
              >
                Replay preview
              </button>
            </div>
          )}
        </div>
      )}

      {/* Audio Fallback Visualization */}
      {useAudioFallback && (
        <div className="p-4">
          <div className="w-full h-12 bg-gray-900/50 rounded-lg flex items-center justify-center space-x-1 px-4">
            {renderStaticBars()}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="p-3 sm:p-4">
        {/* Mobile-first: Stack controls vertically on small screens */}
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
          {/* Top row: Play button and progress bar */}
          <div className="flex items-center space-x-3 sm:space-x-4 flex-1">
            {/* Play/Pause Button */}
            <button 
              onClick={togglePlayPause}
              className="bg-yellow-400 text-black hover:bg-yellow-500 p-2 sm:p-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 shadow-lg hover:shadow-xl flex-shrink-0"
            >
              {isPlaying ? <Pause className="h-4 w-4 sm:h-5 sm:w-5" /> : <Play className="h-4 w-4 sm:h-5 sm:w-5" />}
            </button>

            {/* Progress Bar */}
            <div className="flex-1 min-w-0">
              <div 
                className="w-full h-2 bg-gray-600 rounded-full cursor-pointer"
                onClick={handleProgressClick}
              >
                <div 
                  className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Bottom row: Time and controls */}
          <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-4">
            {/* Time Display */}
            <div className="text-xs sm:text-sm text-gray-300 font-mono whitespace-nowrap">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>

            {/* Control Icons */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button 
                onClick={toggleMute}
                className="text-yellow-400 hover:text-white transition-colors"
                aria-pressed={isMuted}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" /> : <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />}
              </button>
              
              {!useAudioFallback && (
                <button 
                  onClick={toggleFullscreen}
                  className="text-yellow-400 hover:text-white transition-colors"
                  title="Fullscreen"
                >
                  <Maximize className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              )}
              
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Audio Element for Fallback */}
      <audio 
        ref={audioRef}
        preload="metadata"
        style={{ display: 'none' }}
      >
        <source src={fallbackAudioSrc} type="audio/mpeg" />
        <source src={fallbackAudioSrc} type="audio/wav" />
        <source src={fallbackAudioSrc} type="audio/mp4" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default CustomVideoPlayer;
